/* eslint-disable react-hooks/exhaustive-deps */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import ModalContainer from "components/ModalContainer";
import ModalLayout from "components/ModalLayout";
import { Tabs, Tab, TabPanel } from "components/Tab";
import Table from "components/Table";
import configData from "config/config.json";
import useConsent from "hooks/useConsent";
import useOpenClose from "hooks/useOpenClose";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteCardBG, getCardBGs } from "services/cardBGServices";
import sessionHelper from "services/sessionHelper";
import useStyles from "styles/store/storeEditor";
import { PermittedRoles } from "utils/permissions";
import { getCardBGTableColumns } from "utils/tableHelper/extraTableHelper";

const RowActions = ({ data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { handleDelete } = actionHandlers;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.cardBackground.edit?.replace(":id", data?._id));
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.CARD_BACKGROUND.DELETE_ROLES
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={() => handleDelete(data)}>Delete</MenuItem>
        )}
      </Menu>
    </>
  );
};
const CardBackground = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const { isOpen, extraData, open, close, updateExtra } = useOpenClose();
  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.CARD_BACKGROUND.UPDATE_ROLES
  );

  const { pageNo, pageSize } = usePaginationHook({
    totalItems: filteredList.length,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getCardBGs({
        pageNo: 1,
        pageSize: pageSize,
        skipPagination: true,
        sortDir: "asc",
      });

      setFilteredList(resp.data);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const createPlatform = () =>
    navigate(configData.routes.cardBackground.create);

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.name ?? data?._id, id: data?._id });
  };

  const handlePreviewModal = (data: any) => {
    open();
    const { cardImg, cardImgSm, detImg } = filteredList.find(
      (el) => el?._id === data?._id
    );
    updateExtra({ cardImg, cardImgSm, detImg });
  };

  const onDelete = async () => {
    try {
      const resp = await deleteCardBG(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const initialColumns = getCardBGTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission
    );
    setColumns(initialColumns);

    fetchData();
  }, [pageNo, pageSize, hasEditPermission]);

  return (
    <ContentShell
      title="Card Backgrounds"
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <CustomButton onClick={createPlatform}>Create</CustomButton>
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          actions={RowActions}
          highlightAltRow={true}
          actionHandlers={{
            handleDelete: _openConsentDialog,
            openModal: handlePreviewModal,
          }}
        />
        <ModalContainer openModel={isOpen} handleClose={close}>
          <ModalLayout isLargeContainer={true} hasPadding={true} padding={35}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Card Image" value={0} />
              <Tab label="Card Sm. Image" value={1} />
              <Tab label="Details BG" value={2} />
            </Tabs>
            {["cardImg", "cardImgSm", "detImg"].map((image, index) => {
              if (!extraData[image]) {
                return (
                  <TabPanel
                    key={image}
                    style={{ display: "flex", justifyContent: "center" }}
                    value={activeTab}
                    index={index}
                  >
                    No Image Found
                  </TabPanel>
                );
              }
              return (
                <TabPanel
                  key={image}
                  style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                  value={activeTab}
                  index={index}
                >
                  <Box
                    component="img"
                    src={extraData[image]}
                    alt={`${image} Image Preview`}
                    sx={{
                      maxWidth: image === "detImg" ? "400px" : "80%",
                      maxHeight: image === "detImg" ? "400px" : "600px",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </TabPanel>
              );
            })}
          </ModalLayout>
        </ModalContainer>
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
      </Box>
    </ContentShell>
  );
};

export default CardBackground;
